/**
 * PublicTrialSimulator.jsx
 * Componente reutilizable para el simulador público (sin login).
 *
 * Props:
 *   trialKey {string} — 'b2b', 'startup', etc. Debe coincidir con TRIALS_CONFIG
 *
 * Flujo:
 *   1. Pide token guest a /api/public-trial/token
 *   2. Inicia sesión en /api/vertex/public/session/start
 *   3. Procesa turnos en /api/vertex/public/session/turn
 *   4. Al llegar a maxTurns → muestra CTA de registro
 */
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Send, ArrowRight, ArrowLeft, RefreshCw, AlertTriangle, Check,
  ChevronDown, ChevronUp, Users
} from 'lucide-react';
import { TRIALS_CONFIG } from './trialsConfig';

const VERTEX_PUBLIC_BASE = '/api/vertex/public';
const TRIAL_TOKEN_BASE = '/api/public-trial';

// ── Seguridad de input ───────────────────────────────────────────────────────
const MAX_INPUT_CHARS = 200;

// Sanitiza texto plano: elimina etiquetas HTML, bytes de control y
// normaliza espacios. No bloquea caracteres de idioma (tildes, ñ, etc.).
function sanitizeInput(raw) {
  return raw
    .replace(/<[^>]*>/g, '')                         // strip HTML/script tags
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '') // bytes de control
    .replace(/\n{3,}/g, '\n\n')                      // max 2 saltos seguidos
    .slice(0, MAX_INPUT_CHARS);                      // hard limit
}

function isTokenValid(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now() + 60_000;
  } catch {
    return false;
  }
}

function getStoredToken(trialKey) {
  const raw = sessionStorage.getItem(`pt_token_${trialKey}`);
  if (raw && isTokenValid(raw)) return raw;
  sessionStorage.removeItem(`pt_token_${trialKey}`);
  return null;
}

function storeToken(trialKey, token) {
  sessionStorage.setItem(`pt_token_${trialKey}`, token);
}

// ── Formateador del contexto narrativo ───────────────────────────────────────
// Parsea el texto plano en bloques con jerarquía:
//   1° bloque  → situación / escenario
//   Penultimate → "Tu reto es..." con acento
//   Pregunta    → destacada como CTA
//   Instrucción → gris suave debajo de la pregunta
function renderContextText(text) {
  if (!text) return null;

  // Extraer pregunta (¿...?)
  const qMatch = text.match(/(¿[^?]+\?)/);
  const question = qMatch ? qMatch[1] : null;

  // Texto antes de la pregunta
  const beforeQ = question
    ? text.slice(0, text.indexOf(question)).trim()
    : text.trim();

  // Texto después de la pregunta (instrucción tipo "Responde como...")
  const afterQ = question
    ? text.slice(text.indexOf(question) + question.length).trim().replace(/^\.?\s*/, '')
    : '';

  // Partir en oraciones (punto + espacio)
  const sentences = beforeQ
    .split(/\.\s+/)
    .map(s => s.trim())
    .filter(s => s.length > 5);

  // La última oración suele ser el reto personal ("Tu reto es...")
  const retaSentence = sentences.length > 1 ? sentences[sentences.length - 1] : null;
  const contextSentences = retaSentence ? sentences.slice(0, -1) : sentences;

  return (
    <div className="space-y-3">
      {/* Oraciones de contexto */}
      {contextSentences.map((s, i) => (
        <p key={i} className="text-[13px] text-slate-500 leading-relaxed">
          {s}.
        </p>
      ))}

      {/* Reto / llamada a la acción — acento con borde izquierdo */}
      {retaSentence && (
        <p className="text-[13px] font-semibold text-slate-700 leading-snug pl-3 border-l-2 border-[#1E3A8A]">
          {retaSentence}.
        </p>
      )}

      {/* Pregunta — caja destacada */}
      {question && (
        <div
          className="rounded-xl px-4 py-3 mt-1"
          style={{ background: 'rgba(30,58,138,0.07)', border: '1px solid rgba(30,58,138,0.15)' }}
        >
          <p className="text-[14px] font-bold text-[#1E3A8A] leading-snug">
            {question}
          </p>
          {afterQ && (
            <p className="text-[11px] text-slate-400 italic mt-1.5">{afterQ}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function PublicTrialSimulator({ trialKey }) {
  const cfg = TRIALS_CONFIG[trialKey];

  const [phase, setPhase] = useState('loading'); // 'loading' | 'chat' | 'done' | 'error'
  const [token, setToken] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [completedTurns, setCompletedTurns] = useState(0);
  const [maxTurns, setMaxTurns] = useState(cfg?.maxTurns ?? 2);
  const [errorMsg, setErrorMsg] = useState('');
  const [contextText, setContextText] = useState('');
  const [moderationAlert, setModerationAlert] = useState('');
  const [contextCollapsed, setContextCollapsed] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);  // bottom sheet contexto mobile
  const [contextViewed, setContextViewed] = useState(false); // tracking paso 1
  const moderationTimerRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const chatBodyRef = useRef(null);
  const initGenRef = useRef(0);

  // ── Helpers de scroll ──────────────────────────────────────────────────────
  function isNearBottom() {
    const el = chatBodyRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }

  function scrollToBottom(force = false) {
    const el = chatBodyRef.current;
    if (!el) return;
    if (force || isNearBottom()) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }

  // ── Auto-resize textarea ──────────────────────────────────────────────────
  function autoResize() {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }

  useEffect(() => {
    scrollToBottom();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    if (!cfg) {
      setPhase('error');
      setErrorMsg('Configuración de simulador no encontrada.');
      return;
    }
    initSession();
    return () => {
      initGenRef.current += 1;
      clearTimeout(moderationTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialKey]);

  async function initSession() {
    const myGen = initGenRef.current + 1;
    initGenRef.current = myGen;
    const stale = () => initGenRef.current !== myGen;

    setPhase('loading');
    setSessionId(null);
    try {
      let guestToken = getStoredToken(trialKey);
      if (!guestToken) {
        const tokenRes = await fetch(`${TRIAL_TOKEN_BASE}/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ trialKey })
        });
        const tokenData = await tokenRes.json();
        if (stale()) return;
        if (!tokenData.success) throw new Error(tokenData.message || 'Error al obtener acceso.');
        guestToken = tokenData.token;
        storeToken(trialKey, guestToken);
      }
      if (stale()) return;
      setToken(guestToken);

      const startRes = await fetch(`${VERTEX_PUBLIC_BASE}/session/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${guestToken}`
        },
        body: JSON.stringify({})
      });
      const startData = await startRes.json();
      if (stale()) return;
      if (!startData.success) throw new Error(startData.message || 'Error al iniciar simulación.');

      setSessionId(startData.sessionId);
      setMaxTurns(startData.maxTurns ?? cfg.maxTurns);
      setCompletedTurns(0);

      const briefText = startData.caseDescription || startData.briefing?.content || startData.onboarding?.message || '';
      setContextText(briefText);
      setMessages([]);
      setPhase('chat');

    } catch (err) {
      if (stale()) return;
      console.error('[PublicTrial] Error al iniciar sesión:', err);
      setPhase('error');
      setErrorMsg(err.message || 'Error al conectar. Intenta de nuevo.');
    }
  }

  async function sendMessage() {
    const text = sanitizeInput(input).trim();
    if (!text || text.length < 3 || sending || !sessionId || !token) return;

    const userMsg = { role: 'user', text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    // Resetear altura del textarea tras enviar
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    setSending(true);
    setModerationAlert('');

    try {
      const res = await fetch(`${VERTEX_PUBLIC_BASE}/session/turn`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ sessionId, message: text })
      });
      const data = await res.json();

      if (!data.success && data.sessionClosed) {
        setPhase('done');
        return;
      }

      if (data.status === 'armed_to_close') {
        setMessages(prev => prev.slice(0, -1));
        setPhase('done');
        return;
      }

      if (data.status === 'blocked') {
        setMessages(prev => prev.slice(0, -1));
        setInput(text);
        const alertMsg = data.message || data.moderatorAlert?.message || 'Ese mensaje no puede enviarse. Reformúlalo para continuar.';
        setModerationAlert(alertMsg);
        clearTimeout(moderationTimerRef.current);
        moderationTimerRef.current = setTimeout(() => setModerationAlert(''), 5000);
        return;
      }

      if (!data.success) throw new Error(data.message || 'Error al procesar respuesta.');

      const aiText = data.response || data.messages?.[0]?.text || '...';
      setMessages(prev => [...prev, { role: 'client', text: aiText }]);

      const newCompleted = data.counters?.completedTurns ?? data.progress?.currentTurn ?? completedTurns + 1;
      setCompletedTurns(newCompleted);

      if (data.isComplete || data.sessionStatus === 'completed' || newCompleted >= maxTurns) {
        setPhase('done');
      }

    } catch (err) {
      console.error('[PublicTrial] Error en turno:', err);
      setMessages(prev => [...prev, { role: 'system', text: 'Error al procesar tu mensaje. Intenta de nuevo.' }]);
    } finally {
      setSending(false);
    }
  }

  // PC: Enter envía. Mobile: solo botón (no queremos enviar con Enter en téclado virtual)
  function handleKeyDown(e) {
    const isMobile = window.innerWidth < 1024;
    if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
      e.preventDefault();
      sendMessage();
    }
  }

  function handleRetry() {
    sessionStorage.removeItem(`pt_token_${trialKey}`);
    setMessages([]);
    setSessionId(null);
    setCompletedTurns(0);
    initSession();
  }

  // Comportamiento mobile al enfocar el input (spec §13)
  function handleInputFocus() {
    setContextCollapsed(true);
    setSheetOpen(false);
    setTimeout(() => {
      scrollToBottom(true);
      inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 250);
  }

  if (!cfg) {
    return (
      <div className="min-h-[400px] flex items-center justify-center text-slate-500 text-sm">
        Simulador no configurado.
      </div>
    );
  }

  // ─── Estados simples (fuera del layout de dos columnas) ───────────────────

  if (phase === 'loading') {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-3 py-16 text-slate-400 text-sm">
          <div className="w-5 h-5 border-2 border-slate-200 border-t-[#0c203b] rounded-full animate-spin" />
          Iniciando simulación...
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="border border-slate-200 rounded-xl p-8 space-y-4 w-full max-w-sm">
          <p className="text-slate-600 text-sm">{errorMsg}</p>
          <button
            onClick={initSession}
            className="inline-flex items-center gap-2 text-sm text-[#0c203b] hover:underline"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  // ─── Layout principal: dos columnas ───────────────────────────────────────

  return (
    <div className="h-full">
          {/* Bottom sheet contexto — mobile only */}
      {sheetOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setSheetOpen(false)}
          />
          {/* Sheet */}
          <div
            className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white flex flex-col"
            style={{
              height: '82dvh',
              borderRadius: '20px 20px 0 0',
              boxShadow: '0 -8px 32px rgba(0,0,0,0.18)'
            }}
          >
            {/* Handle — tap para cerrar */}
            <button
              className="flex justify-center pt-3 pb-1 shrink-0 w-full"
              onClick={() => setSheetOpen(false)}
              aria-label="Cerrar escenario"
            >
              <div className="w-10 h-1 rounded-full bg-slate-200" />
            </button>

            {/* ── Cabecera Artemis — compacta horizontal ───────────────── */}
            <div
              className="mx-4 mt-2 mb-3 rounded-2xl flex items-center gap-3 px-4 py-3 shrink-0"
              style={{ background: '#0c203b' }}
            >
              <div
                className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0"
                style={{ boxShadow: '0 0 0 2px rgba(255,255,255,0.15), 0 4px 12px rgba(0,0,0,0.4)' }}
              >
                <img src="/artemis.png" alt="Artemis" className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-[13px] leading-tight">Artemis</p>
                <p className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase mt-0.5">Coach IA</p>
              </div>
              <p className="text-slate-400 text-[11px] italic leading-snug max-w-[140px] text-right">
                "Revisa antes de responder."
              </p>
            </div>

            {/* ── Contenido scrollable ───────────────────────────────── */}
            <div className="flex-1 overflow-y-auto px-5 pb-2 space-y-4">
              {contextText && renderContextText(contextText)}
              {cfg.tensions && cfg.tensions.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase mb-2">Tensión actual</p>
                  <ul className="space-y-2">
                    {cfg.tensions.map((t, i) => (
                      <li key={i} className="flex items-start gap-2 text-[13px] text-slate-600">
                        <span className="mt-[4px] w-1.5 h-1.5 rounded-full bg-[#1E3A8A] shrink-0" />
                        <span><span className="font-semibold text-slate-700">{t.area}</span> — {t.issue}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex items-center gap-2 text-[13px] text-slate-500 pt-2 border-t border-slate-100">
                <Users className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                Tu rol: Vendedor senior
              </div>
            </div>

            {/* ── CTA fijo al pie del sheet ───────────────────────────── */}
            <div
              className="px-4 py-4 border-t border-slate-100 flex-shrink-0 space-y-2"
              style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
            >
              <button
                onClick={() => {
                  setSheetOpen(false);
                  setTimeout(() => inputRef.current?.focus(), 200);
                }}
                className="w-full h-12 bg-[#1E3A8A] hover:bg-[#17316F] active:bg-[#0f2460] text-white font-semibold text-[14px] rounded-2xl transition-colors flex items-center justify-center gap-2"
                style={{ boxShadow: '0 4px 14px rgba(30,58,138,0.35)' }}
              >
                <Send className="w-4 h-4" />
                Responder ahora
              </button>
              <button
                onClick={() => setSheetOpen(false)}
                className="w-full h-10 text-slate-500 text-[13px] font-medium flex items-center justify-center gap-1.5 rounded-xl hover:bg-slate-50 active:bg-slate-100 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Volver al chat
              </button>
            </div>
          </div>
        </>
      )}
      <div className="flex flex-col lg:flex-row overflow-hidden h-full border-t border-[#E5E7EB] lg:border lg:rounded-xl lg:shadow-[0_4px_12px_rgba(0,0,0,0.06)]">

        {/* ── COLUMNA IZQUIERDA — Contexto del caso (35%) ──────────────────── */}
        {/* MOBILE: hidden. Contexto accesible via drawer en el header del chat. */}
        <div className="hidden lg:flex lg:flex-col lg:order-1 lg:w-[35%] border-r border-[#E5E7EB] bg-[#F1F5F9] border-l-[3px] border-l-[#0c203b] overflow-hidden">

          {/* ── Artemis — Coach IA ──────────────────────────────────────── */}
          <div className="bg-[#0c203b] px-5 pt-7 pb-6 shrink-0 flex flex-col items-center text-center">
            <div
              className="w-[76px] h-[76px] rounded-full overflow-hidden mb-3"
              style={{ boxShadow: '0 0 0 2px rgba(255,255,255,0.13), 0 0 0 5px rgba(255,255,255,0.05), 0 8px 20px rgba(0,0,0,0.45)' }}
            >
              <img
                src="/artemis.png"
                alt="Artemis"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <p className="text-white font-semibold text-[15px] leading-tight tracking-tight">Artemis</p>
            <p className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase mt-1">
              Coach IA · Negociación B2B
            </p>
            <p className="text-slate-400 text-[11px] leading-[1.65] mt-3 italic px-3">
              "Revisa el contexto antes de responder. Cada detalle define el resultado."
            </p>
          </div>

          {/* Degradado de transición */}
          <div className="h-px bg-gradient-to-r from-transparent via-slate-300/50 to-transparent shrink-0" />

          {/* ── Datos del caso (scrollable) ─────────────────────────────── */}
          <div className="flex flex-col gap-5 px-5 py-5 overflow-y-auto flex-1">

            {/* Descripción del caso formateada */}
            {contextText && renderContextText(contextText)}

            {/* Tensiones configuradas */}
            {cfg.tensions && cfg.tensions.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase mb-2">
                  Tensión actual
                </p>
                <ul className="space-y-1.5">
                  {cfg.tensions.map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-[12px] text-slate-600">
                      <span className="mt-[3px] w-1.5 h-1.5 rounded-full bg-[#0c203b] shrink-0" />
                      <span><span className="font-medium text-slate-700">{t.area}</span> — {t.issue}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Rol del participante */}
            <div className="pt-1 border-t border-slate-200">
              <p className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase mb-1.5">
                Tu rol
              </p>
              <div className="flex items-center gap-2 text-[12px] text-slate-600">
                <Users className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                Vendedor senior
              </div>
            </div>

          </div>
        </div>

        {/* ── COLUMNA DERECHA — Simulación (65%) ───────────────────────────── */}
        {/* MOBILE: ocupa full width, es la única columna visible. */}
        <div className="flex-1 w-full lg:order-2 lg:w-[65%] flex flex-col bg-white min-h-0">

          {/* Header del chat — mobile: Kuro + label + "Ver contexto" */}
          <div
            className="px-3 lg:px-5 border-b border-[#E5E7EB] flex items-center justify-between flex-shrink-0"
            style={{ height: '52px' }}
          >
            <div className="flex items-center gap-2.5">
              {/* Avatar Kuro — solo mobile, solo antes de que haya mensajes */}
              {messages.length === 0 && (
                <img
                  src="/kuro.png"
                  alt="Cliente IA"
                  className="lg:hidden w-8 h-8 rounded-full object-cover object-top flex-shrink-0 border border-slate-200"
                />
              )}
              <div>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#10B981] flex-shrink-0" />
                  <span className="text-[12px] font-semibold text-slate-600 uppercase tracking-wider">
                    {cfg.clientLabel || 'Cliente (Comité comprador)'}
                  </span>
                </div>

              </div>
            </div>
            {/* Píldora — solo visible DESPUÉS de leer el contexto, como acceso rápido */}
            {contextViewed && (
              <button
                className="lg:hidden flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full transition-colors active:bg-emerald-100"
                onClick={() => setSheetOpen(true)}
              >
                <span>Escenario</span>
                <ChevronDown className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Strip contextual — solo visible durante conversación activa (recordatorio pasivo) */}
          {cfg.blockedDecision && messages.length > 0 && phase === 'chat' && (
            <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
              <span className="text-[11px] text-slate-400 leading-snug truncate">
                <span className="font-semibold text-slate-500">{cfg.blockedDecision.label}</span>
                {' '}·{' '}{cfg.blockedDecision.description}
              </span>
            </div>
          )}

          {/* Área de mensajes */}
          <div
            ref={chatBodyRef}
            className="flex-1 overflow-y-auto px-3 lg:px-4 py-4 space-y-3 lg:space-y-4"
            style={{ WebkitOverflowScrolling: 'touch', paddingBottom: '16px' }}
          >
            {messages.length === 0 && phase === 'chat' && (
              <div className="flex flex-col items-center px-4" style={{ marginTop: '16px' }}>

                {/* ── PASO 1: Ver contexto — solo mobile (desktop ya lo ve en panel izquierdo) ── */}
                <div className="lg:hidden w-full max-w-sm">
                  {!contextViewed ? (
                    /* Tarjeta oscura — CTA de alto peso visual */
                    <button
                      className="w-full text-left rounded-2xl px-5 py-5 active:opacity-90 transition-opacity"
                      style={{ background: '#0c203b', boxShadow: '0 4px 24px rgba(8,24,39,0.30)' }}
                      onClick={() => { setSheetOpen(true); setContextViewed(true); }}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src="/artemis.png"
                          alt="Artemis"
                          className="w-11 h-11 rounded-full object-cover object-top flex-shrink-0"
                          style={{ boxShadow: '0 0 0 2px rgba(255,255,255,0.18)' }}
                        />
                        <div>
                          <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: 'rgba(148,163,184,0.8)' }}>Paso 1 · Antes de responder</p>
                          <p className="text-white font-bold text-[15px] leading-tight mt-0.5">Lee el contexto del caso</p>
                        </div>
                      </div>
                      <p className="text-[12px] leading-relaxed mb-4" style={{ color: 'rgba(148,163,184,0.9)' }}>
                        Artemis preparó el escenario. Entiéndelo antes de hablar con el cliente.
                      </p>
                      <div
                        className="w-full h-11 rounded-xl flex items-center justify-center gap-2 font-semibold text-[13px] text-white"
                        style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}
                      >
                        Ver contexto del caso
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </button>
                  ) : (
                    /* Badge verde — confirma paso completado */
                    <div className="w-full flex items-center gap-3 rounded-xl px-4 py-3 border border-emerald-200 bg-emerald-50">
                      <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div>
                        <p className="text-[13px] font-semibold text-emerald-700 leading-tight">Contexto revisado</p>
                        <p className="text-[11px] text-emerald-600/70 mt-px">Ya puedes escribir tu respuesta</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Separador visual entre pasos — solo mobile */}
                <div className="lg:hidden flex flex-col items-center my-3">
                  <div className="w-px h-5 bg-gradient-to-b from-slate-300 to-slate-200" />
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-200 mt-0.5" />
                </div>

                {/* ── PASO 2: Misión — se atenúa en mobile si no se leyó contexto ── */}
                <div
                  className={`w-full max-w-sm bg-white rounded-2xl border px-5 py-5 transition-all ${
                    contextViewed
                      ? 'border-slate-200 opacity-100'
                      : 'border-slate-100 lg:opacity-100 opacity-40'
                  }`}
                  style={{ boxShadow: contextViewed ? '0 2px 12px rgba(0,0,0,0.07)' : 'none' }}
                >
                  <p className="text-[10px] font-bold tracking-widest text-slate-400 uppercase mb-3">
                    {contextViewed ? 'Paso 2 · ' : ''}Tu misión
                  </p>
                  <p className="text-[14px] font-semibold text-slate-700 leading-snug">
                    {cfg.blockedDecision?.description || 'Destraba la decisión antes de que el deal se congele.'}
                  </p>
                  <div className="mt-4 pt-3 border-t border-slate-100 space-y-1.5">
                    <div className="flex items-start gap-2 text-[12px] text-slate-500">
                      <span className="w-4 h-4 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500 shrink-0 mt-px">1</span>
                      Identifica qué está frenando la decisión
                    </div>
                    <div className="flex items-start gap-2 text-[12px] text-slate-500">
                      <span className="w-4 h-4 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500 shrink-0 mt-px">2</span>
                      Escribe tu respuesta abajo como si estuvieras en la reunión
                    </div>
                  </div>
                </div>

                {/* ── Flecha hacia el input ── */}
                <div className="flex flex-col items-center mt-3 gap-1">
                  <div
                    className="w-px bg-gradient-to-b from-slate-300 to-transparent"
                    style={{ height: '22px' }}
                  />
                  <ChevronDown className="w-4 h-4 text-slate-300" />
                  <p className="text-[11px] text-slate-400 mt-0.5">Responde abajo</p>
                </div>

              </div>
            )}

            {messages.map((msg, i) => (
              msg.role === 'client' ? (
                // ── Cliente IA — burbuja con avatar Kuro ─────────────────
                <div key={i} className="flex items-end gap-2.5">
                  <img
                    src="/kuro.png"
                    alt="Cliente IA"
                    className="w-8 h-8 rounded-full object-cover object-top flex-shrink-0 border border-slate-200 shadow-sm"
                  />
                  <div className="flex flex-col items-start min-w-0">
                    <span className="text-[10px] text-slate-400 mb-1 ml-1">
                      {cfg.clientLabel || 'Cliente'}
                    </span>
                    <div className="max-w-full px-4 py-3 text-[13px] leading-relaxed rounded-lg bg-white text-slate-700 border border-[#E5E7EB] shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
                      {msg.text}
                    </div>
                  </div>
                </div>
              ) : (
                // ── Usuario / sistema ─────────────────────────────────────
                <div key={i} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                  {msg.role === 'user' && (
                    <span className="text-[10px] text-slate-400 mb-1 mr-0.5">Tú</span>
                  )}
                  <div
                    className={`max-w-[82%] px-4 py-3 text-[13px] leading-relaxed rounded-lg ${
                      msg.role === 'user'
                        ? 'bg-[#1E3A8A] text-white'
                        : 'bg-red-50 text-red-600 border border-red-100 text-xs'
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              )
            ))}

            {sending && (
              // ── Typing indicator con avatar Kuro ────────────────────────
              <div className="flex items-end gap-2.5">
                <img
                  src="/kuro.png"
                  alt="Cliente IA"
                  className="w-8 h-8 rounded-full object-cover object-top flex-shrink-0 border border-slate-200 shadow-sm"
                />
                <div className="flex flex-col items-start">
                  <span className="text-[10px] text-slate-400 mb-1 ml-1">
                    {cfg.clientLabel || 'Cliente'}
                  </span>
                  <div className="bg-white border border-[#E5E7EB] shadow-[0_1px_3px_rgba(0,0,0,0.06)] px-4 py-3 rounded-lg text-slate-400 text-[13px]">
                    <span className="animate-pulse">···</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input — fixed al pie en mobile, relativo en desktop */}
          {phase === 'chat' && (
            <div
              className="flex-shrink-0"
              style={{
                borderTop: '2px solid rgba(30,58,138,0.18)',
                background: '#FFFFFF',
                paddingBottom: 'env(safe-area-inset-bottom, 0px)'
              }}
            >
              {/* Alerta de moderación */}
              {moderationAlert && (
                <div className="flex items-start gap-2 px-4 pt-3 pb-2 text-[12px] text-amber-700 bg-amber-50 border-b border-amber-100">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span className="leading-snug">{moderationAlert}</span>
                </div>
              )}
              {/* Label pregunta — siempre visible, ancla la acción */}
              <div className="px-4 pt-3 pb-2">
                <p className="text-[13px] font-semibold text-slate-700 leading-tight">
                  ¿Cómo responderías en este momento?
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Escribe como si estuvieras en la reunión
                </p>
              </div>
              {/* Textarea + botón */}
              <div className="px-4 pb-4">
                <div className="relative flex items-end gap-2">
                  <div className="flex-1 flex flex-col">
                    <textarea
                      value={input}
                      onChange={e => {
                        const sanitized = sanitizeInput(e.target.value);
                        setInput(sanitized);
                        autoResize();
                      }}
                      onKeyDown={handleKeyDown}
                      placeholder="Escribe tu respuesta…"
                      rows={1}
                      ref={inputRef}
                      onFocus={handleInputFocus}
                      maxLength={MAX_INPUT_CHARS}
                      className="resize-none text-slate-800 placeholder-gray-400 bg-[#F8FAFC] border border-slate-200 rounded-2xl px-4 outline-none hover:border-slate-300 focus:border-[#1E3A8A] focus:bg-white focus:shadow-[0_0_0_3px_rgba(30,58,138,0.12)] transition-all overflow-y-auto"
                      style={{
                        fontSize: '16px',
                        lineHeight: '1.5',
                        minHeight: '52px',
                        maxHeight: '120px',
                        paddingTop: '16px',
                        paddingBottom: '16px'
                      }}
                      disabled={sending}
                    />
                    {/* Contador — solo visible al acercarse al límite */}
                    {input.length >= MAX_INPUT_CHARS * 0.8 && (
                      <p className={`text-right text-[10px] mt-1 pr-1 ${
                        input.length >= MAX_INPUT_CHARS ? 'text-red-400 font-semibold' : 'text-slate-400'
                      }`}>
                        {input.length}/{MAX_INPUT_CHARS}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={sendMessage}
                    disabled={!input.trim() || sending}
                    className="w-11 h-11 flex items-center justify-center bg-[#1E3A8A] hover:bg-[#17316F] disabled:bg-slate-200 disabled:cursor-not-allowed text-white disabled:text-slate-400 rounded-full transition-all flex-shrink-0"
                    style={{ boxShadow: input.trim() ? '0 4px 12px rgba(30,58,138,0.35)' : 'none' }}
                  >
                    {sending
                      ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Resultado al finalizar */}
          {phase === 'done' && (
            <div
              className="border border-[#E5E7EB] rounded-lg mx-4 my-5 px-5 py-5 space-y-4"
              style={{ background: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
            >
              {/* Etiqueta de resultado */}
              <div className="space-y-2">
                <p className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase">
                  Resultado
                </p>
                {cfg.outcome ? (
                  <div className="space-y-1.5">
                    <p className="text-[#111827] font-bold text-[16px] leading-snug">
                      {cfg.outcome.label}
                    </p>
                    <p className="text-slate-500 text-[13px] leading-relaxed">
                      {cfg.outcome.description}
                    </p>
                    {cfg.outcome.footnote && (
                      <p className="text-[#1E3A8A] text-[13px] font-semibold pt-1">
                        {cfg.outcome.footnote}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-[#111827] font-bold text-[16px]">
                    Sesión completada.
                  </p>
                )}
              </div>

              {/* Divisor */}
              <div className="border-t border-[#E5E7EB] pt-4 space-y-3">
                {cfg.ctaHeadline && (
                  <p className="text-slate-700 text-[13px] font-medium">{cfg.ctaHeadline}</p>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <Link
                    to={cfg.ctaLink}
                    className="inline-flex items-center justify-center gap-2 h-10 px-5 bg-[#1E3A8A] hover:bg-[#17316F] text-white text-[13px] font-semibold rounded-lg transition-colors"
                  >
                    {cfg.ctaButtonText}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                  <button
                    onClick={handleRetry}
                    className="inline-flex items-center justify-center gap-2 h-10 px-4 text-slate-500 hover:text-slate-700 text-[13px] border border-[#E5E7EB] hover:border-slate-300 rounded-lg transition-colors"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Reintentar
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

